import { AxiosError } from 'axios';

const BIDDING_ERROR_MESSAGES_REGEX = {
	BID_ALREADY_PLACED: /Cancel the existing bid/i,
	BID_FLOOR: /Bid below minimum/i,
	DEALER_OWNS_VEHICLE: /Dealers can not bid on their own vehicles/i,
	INVALID_FORMAT: /params\['[a-z]+'\] should be a/i,
};

export const CSV_ERRORS = {
	FAILED_TO_FETCH_VEHICLES: 'Vehicle list fetch failed',
	FAILED_TO_PARSE: 'CSV parse error',
	INVALID_FORMAT: 'Invalid format',
	NO_BIDS: 'No bids in CSV',
	NO_LIVE_SALE_VEHICLES: 'No live sale vehicles',
	TOO_MANY_BIDS: 'Too many bids in CSV',
} as const;

export const BULK_BIDDING_ERROR_TYPES = {
	ABORTED: 'Request aborted by user',
	BID_ALREADY_PLACED: 'Bid already placed',
	BID_FLOOR: 'Minimum bid floor hit',
	DEALER_OWNS_VEHICLE: 'Dealer owns vehicle',
	GENERAL_ERROR: 'General error',
	INVALID_FORMAT: 'Invalid format',
} as const;

export type BulkBiddingErrorType = (typeof BULK_BIDDING_ERROR_TYPES)[keyof typeof BULK_BIDDING_ERROR_TYPES];

export const hasErrorMessageInResponse = (error: AxiosError): error is AxiosError<{ message: string }> => {
	return Boolean(
		'response' in error &&
			error.response?.data &&
			typeof error.response.data === 'object' &&
			'message' in error.response.data &&
			typeof error.response.data.message === 'string',
	);
};

export const isRepeatableError = (error: AxiosError) => {
	if (!hasErrorMessageInResponse(error) || !error.response?.data.message) return false;
	const { message } = error.response.data;
	return (
		BIDDING_ERROR_MESSAGES_REGEX.BID_FLOOR.test(message) ||
		BIDDING_ERROR_MESSAGES_REGEX.BID_ALREADY_PLACED.test(message) ||
		BIDDING_ERROR_MESSAGES_REGEX.INVALID_FORMAT.test(message) ||
		BIDDING_ERROR_MESSAGES_REGEX.DEALER_OWNS_VEHICLE.test(message)
	);
};

export const isCancelledAxiosError = (error: unknown) =>
	error instanceof AxiosError && error.code === AxiosError.ERR_CANCELED;

export const getBiddingErrorReason = (error: AxiosError): BulkBiddingErrorType => {
	if (!hasErrorMessageInResponse(error) || !error.response?.data.message) return BULK_BIDDING_ERROR_TYPES.GENERAL_ERROR;

	const { message } = error.response.data;

	if (BIDDING_ERROR_MESSAGES_REGEX.BID_FLOOR.test(message)) {
		return BULK_BIDDING_ERROR_TYPES.BID_FLOOR;
	}

	if (BIDDING_ERROR_MESSAGES_REGEX.BID_ALREADY_PLACED.test(message)) {
		return BULK_BIDDING_ERROR_TYPES.BID_ALREADY_PLACED;
	}

	// Unlikely as we are doing zod validation but for extra monitoring
	if (BIDDING_ERROR_MESSAGES_REGEX.INVALID_FORMAT.test(message)) {
		return BULK_BIDDING_ERROR_TYPES.INVALID_FORMAT;
	}

	if (BIDDING_ERROR_MESSAGES_REGEX.DEALER_OWNS_VEHICLE.test(message)) {
		return BULK_BIDDING_ERROR_TYPES.DEALER_OWNS_VEHICLE;
	}

	return BULK_BIDDING_ERROR_TYPES.GENERAL_ERROR;
};
